/**
 * CSV, the escape hatch.
 *
 * This gets the data into a spreadsheet or a statistics package without
 * depending on this app. It is deliberately **lossy** — it flattens a domain
 * model into a grid — and it is emphatically not a backup. The backup is
 * T-502's JSON, and the two must not be confused: a user who believes their CSV
 * is a backup finds out otherwise at the worst possible moment.
 *
 * Two shapes, because the two things people do with exported data want opposite
 * layouts:
 *
 *   - **long** — one row per measurement. Complete: every note, every tag,
 *     every captured zone. This is the shape statistics packages want.
 *   - **wide** — one row per entry, one column per track. This is the shape a
 *     spreadsheet wants, and it cannot carry per-measurement notes, because
 *     there is nowhere to put them without a column per track.
 *
 * Here rather than in the app because the native app must produce byte-identical
 * files: an analysis script someone wrote against a phone export should not
 * break when they re-export from the browser.
 */

import type { ResolvedRange } from '../analysis/range.js'
import type { Entry, Measurement, Tag } from '../model/entry.js'
import type { CheckInTemplate } from '../model/template.js'
import { dayKey, toIso8601, wallClockOf } from '../model/timestamp.js'
import {
  describeValue,
  matchTrackKind,
  type Track,
  type TrackValue,
} from '../model/track.js'
import type { ExportFile } from '../ports/exporter.js'

export type CsvShape = 'long' | 'wide'

export interface CsvRequest {
  readonly shape: CsvShape
  /** The entries to export — already filtered to the range by the caller. */
  readonly entries: readonly Entry[]
  /**
   * Every track the entries might reference, **archived ones included**.
   *
   * Filtering to active tracks is how an export silently develops holes: the
   * measurement is still stored, and it exports as nothing.
   */
  readonly tracks: readonly Track[]
  readonly tags?: readonly Tag[]
  readonly templates?: readonly CheckInTemplate[]
  /** Restricts which tracks are exported. Absent exports all of them. */
  readonly trackIds?: readonly string[]
  /** Titles the file. Carried so the filename records what was exported. */
  readonly range: ResolvedRange
}

/**
 * A byte-order mark, so Excel reads the file as UTF-8.
 *
 * Without it Excel on Windows guesses the system code page and mangles every
 * non-ASCII character in a track name or a note. It is emitted here rather than
 * left to each app because "the export is unreadable in Excel" is not a bug
 * anyone attributes to a missing BOM, and the native app would have to
 * rediscover it.
 */
const BOM = '\uFEFF'

const RECORD_SEPARATOR = '\r\n'

/** RFC 4180: a field is quoted when it contains a delimiter, quote or newline. */
const NEEDS_QUOTING = /[",\r\n]/

/**
 * Separates tag names within their single cell.
 *
 * A list inside a cell has no correct answer in CSV. A semicolon is the least
 * bad one: it is not the delimiter, so a naive `split(',')` on the row still
 * works, and it is what spreadsheet import wizards offer as a secondary
 * separator. A tag containing a semicolon is ambiguous — the long format's
 * per-measurement rows are the shape to use when that matters.
 */
const TAG_SEPARATOR = '; '

function field(value: string): string {
  return NEEDS_QUOTING.test(value) ? `"${value.replaceAll('"', '""')}"` : value
}

function row(values: readonly string[]): string {
  return values.map(field).join(',')
}

/** `HH:MM:SS` in the zone the entry was captured in, never the reader's. */
function localTime(entry: Entry): string {
  const wall = wallClockOf(entry.at)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}`
}

/**
 * The value as stored, not as displayed.
 *
 * Enums export their **option id** here and their label separately, so renaming
 * an option later does not invalidate an analysis someone already wrote — the
 * same reason history references options by id rather than by label.
 */
function rawValue(track: Track, value: TrackValue): string {
  return matchTrackKind<string>(track.kind, {
    numeric: () => (typeof value === 'number' ? String(value) : ''),
    boolean: () => (typeof value === 'boolean' ? String(value) : ''),
    enum: () => (typeof value === 'string' ? value : ''),
  })
}

/** Whether the raw value is worth a second column of its own. */
function hasDistinctLabel(track: Track): boolean {
  return matchTrackKind<boolean>(track.kind, {
    numeric: () => false,
    boolean: () => true,
    enum: () => true,
  })
}

interface Context {
  readonly tracksById: Map<string, Track>
  readonly tagsById: Map<string, Tag>
  readonly templatesById: Map<string, CheckInTemplate>
  /** The tracks to export, in their configured order. */
  readonly selected: readonly Track[]
}

function contextFor(request: CsvRequest): Context {
  const filter = request.trackIds === undefined ? null : new Set(request.trackIds)
  const selected = [...request.tracks]
    .filter((track) => filter === null || filter.has(track.id))
    .sort((a, b) => a.position - b.position || a.id.localeCompare(b.id))

  return {
    tracksById: new Map(request.tracks.map((track) => [track.id, track])),
    tagsById: new Map((request.tags ?? []).map((tag) => [tag.id, tag])),
    templatesById: new Map(
      (request.templates ?? []).map((template) => [template.id, template]),
    ),
    selected,
  }
}

/** Ascending by instant, tie-broken by id so two exports of one range match. */
function ordered(entries: readonly Entry[]): Entry[] {
  return [...entries].sort(
    (a, b) => a.at.epochMs - b.at.epochMs || a.id.localeCompare(b.id),
  )
}

function tagNames(entry: Entry, context: Context): string {
  return entry.tagIds
    .map((id) => context.tagsById.get(id)?.name ?? id)
    .join(TAG_SEPARATOR)
}

function templateName(entry: Entry, context: Context): string {
  if (entry.templateId === undefined) {
    return ''
  }
  return context.templatesById.get(entry.templateId)?.name ?? entry.templateId
}

/**
 * The measurements to export for an entry, in the tracks' configured order.
 *
 * Ordering by track rather than by the order they happened to be recorded means
 * the long rows for one entry line up with the wide columns, and means two
 * exports of the same data are identical files.
 */
function selectedMeasurements(
  entry: Entry,
  context: Context,
): { readonly track: Track; readonly measurement: Measurement }[] {
  const byTrack = new Map(entry.measurements.map((m) => [m.trackId, m]))
  const rows: { track: Track; measurement: Measurement }[] = []
  for (const track of context.selected) {
    const measurement = byTrack.get(track.id)
    if (measurement !== undefined) {
      rows.push({ track, measurement })
    }
  }
  return rows
}

const LONG_HEADERS = [
  'entry_id',
  'timestamp_iso',
  'timezone',
  'local_date',
  'local_time',
  'template',
  'track',
  'unit',
  'value',
  'value_label',
  // The measurement's own note, for the rare value that needs explaining.
  'note',
  // The entry's note, repeated on each of its rows — one row per measurement
  // means there is nowhere else to put it, and dropping it would lose the
  // context that describes the moment.
  'entry_note',
  'tags',
] as const

/** The columns the wide shape carries before and after its per-track ones. */
const WIDE_LEADING = [
  'entry_id',
  'timestamp_iso',
  'timezone',
  'local_date',
  'local_time',
  'template',
] as const
const WIDE_TRAILING = ['entry_note', 'tags'] as const
const FIXED_WIDE_HEADERS: readonly string[] = [...WIDE_LEADING, ...WIDE_TRAILING]

function longRows(request: CsvRequest, context: Context): string[] {
  const rows: string[] = [row([...LONG_HEADERS])]

  for (const entry of ordered(request.entries)) {
    const shared = [
      entry.id,
      toIso8601(entry.at),
      entry.at.timeZone,
      dayKey(entry.at),
      localTime(entry),
      templateName(entry, context),
    ]
    const entryNote = entry.note ?? ''
    const tags = tagNames(entry, context)

    for (const { track, measurement } of selectedMeasurements(entry, context)) {
      rows.push(
        row([
          ...shared,
          track.name,
          track.unit ?? '',
          rawValue(track, measurement.value),
          describeValue(track, measurement.value) ?? '',
          measurement.note ?? '',
          entryNote,
          tags,
        ]),
      )
    }

    /*
     * A measurement whose track is missing entirely.
     *
     * `validateEntry` refuses these, so this only happens to a database that
     * has been corrupted or hand-edited. It exports the id in the `track`
     * column rather than being skipped: an export that silently drops rows is
     * worse than one with a row a human has to go and identify.
     *
     * Only when the user did not narrow to specific tracks — a track that does
     * not exist cannot be one they chose from the list of ones that do.
     */
    for (const measurement of request.trackIds === undefined ? entry.measurements : []) {
      if (context.tracksById.has(measurement.trackId)) {
        continue
      }
      rows.push(
        row([
          ...shared,
          measurement.trackId,
          '',
          typeof measurement.value === 'string'
            ? measurement.value
            : String(measurement.value),
          '',
          measurement.note ?? '',
          entryNote,
          tags,
        ]),
      )
    }
  }

  return rows
}

/**
 * Column headers for the wide shape, disambiguated where names repeat.
 *
 * Two active tracks cannot share a name, but an archived one can share with an
 * active one — and history is exported including archived tracks, so the
 * collision is reachable. Two columns with the same header silently merge in
 * some importers, which is exactly the kind of quiet corruption this export
 * exists to avoid.
 */
function wideHeaders(context: Context): {
  readonly headers: string[]
  readonly columns: { readonly track: Track; readonly label: boolean }[]
} {
  const used = new Set(FIXED_WIDE_HEADERS)
  const headers: string[] = []
  const columns: { track: Track; label: boolean }[] = []

  const claim = (base: string, track: Track): string => {
    let name = base
    if (used.has(name)) {
      name = `${base} [${track.id}]`
    }
    let suffix = 2
    while (used.has(name)) {
      name = `${base} [${track.id}] ${String(suffix)}`
      suffix += 1
    }
    used.add(name)
    return name
  }

  for (const track of context.selected) {
    // The unit belongs in the header rather than in every cell: a column of
    // "72 kg" strings is not a column a spreadsheet can average.
    const base = track.unit === undefined ? track.name : `${track.name} (${track.unit})`
    headers.push(claim(base, track))
    columns.push({ track, label: false })

    if (hasDistinctLabel(track)) {
      headers.push(claim(`${track.name} (label)`, track))
      columns.push({ track, label: true })
    }
  }

  return { headers, columns }
}

function wideRows(request: CsvRequest, context: Context): string[] {
  const { headers, columns } = wideHeaders(context)
  const rows: string[] = [row([...WIDE_LEADING, ...headers, ...WIDE_TRAILING])]

  for (const entry of ordered(request.entries)) {
    const measured = new Map(
      selectedMeasurements(entry, context).map(({ track, measurement }) => [
        track.id,
        { track, measurement },
      ]),
    )

    // An entry that measured none of the selected tracks contributes nothing
    // but a row of empty cells, which reads as "logged and skipped everything"
    // rather than "outside this selection".
    if (measured.size === 0) {
      continue
    }

    const cells = columns.map(({ track, label }) => {
      const found = measured.get(track.id)
      // Absent, not zero. A skipped track and a track measured at zero are
      // different claims, and CSV is precisely where that distinction dies.
      if (found === undefined) {
        return ''
      }
      return label
        ? (describeValue(track, found.measurement.value) ?? '')
        : rawValue(track, found.measurement.value)
    })

    rows.push(
      row([
        entry.id,
        toIso8601(entry.at),
        entry.at.timeZone,
        dayKey(entry.at),
        localTime(entry),
        templateName(entry, context),
        ...cells,
        entry.note ?? '',
        tagNames(entry, context),
      ]),
    )
  }

  return rows
}

/** The CSV text, byte-order mark included. */
export function toCsv(request: CsvRequest): string {
  const context = contextFor(request)
  const rows =
    request.shape === 'long' ? longRows(request, context) : wideRows(request, context)
  return BOM + rows.join(RECORD_SEPARATOR) + RECORD_SEPARATOR
}

/** `anytracker-long-2026-06-01-to-2026-06-30.csv`. */
export function csvFilename(shape: CsvShape, range: ResolvedRange): string {
  return `anytracker-${shape}-${range.fromDay}-to-${range.toDay}.csv`
}

/**
 * How many rows the export will contain, excluding the header.
 *
 * Offered so the UI can say what is about to be downloaded. Counting by
 * generating the file and splitting it would be correct and would also build
 * the whole export twice.
 */
export function csvRowCount(request: CsvRequest): number {
  const context = contextFor(request)
  if (request.shape === 'wide') {
    return ordered(request.entries).filter(
      (entry) => selectedMeasurements(entry, context).length > 0,
    ).length
  }
  return request.entries.reduce(
    (total, entry) => total + selectedMeasurements(entry, context).length,
    0,
  )
}

export function buildCsv(request: CsvRequest): ExportFile {
  return {
    filename: csvFilename(request.shape, request.range),
    // The charset is declared as well as marked. Browsers honour the type on a
    // download and some tools read it in preference to sniffing the BOM.
    mimeType: 'text/csv;charset=utf-8',
    content: toCsv(request),
  }
}
